import { useState } from 'react';
import { useChannelsList } from '../api/useChannels.ts';
import {
  useDefaultKeyNamespaces,
  useUpdateDefaultKeyNamespaces,
} from '../api/defaultKeyNamespaces.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Checkbox,
  EmptyState,
  PageHeader,
  Panel,
  Skeleton,
} from '../ui/index.ts';

/**
 * The namespace group every newly created tenant API key is mapped to
 * (design 2026-08-02).
 *
 * The set is platform-wide. A key only ever receives the members its own org
 * and member scope can see, so this page deliberately offers no brand or
 * customer dimension — that filtering happens per key, at creation.
 */
export function DefaultKeyNamespacesPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);

  const configured = useDefaultKeyNamespaces(isAdmin);
  const namespaces = useChannelsList();
  const update = useUpdateDefaultKeyNamespaces();
  const [draft, setDraft] = useState<ReadonlySet<string> | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const saved = configured.data?.namespaces ?? null;
  const savedIds = saved === null ? null : saved.map((row) => row.namespace_id);
  const effective = draft ?? (savedIds === null ? null : new Set(savedIds));
  const liveSkuCounts = new Map(
    (saved ?? []).map((row) => [row.namespace_id, row.live_sku_count] as const),
  );

  function toggle(namespaceId: string, checked: boolean): void {
    const base = new Set(effective ?? []);
    if (checked) base.add(namespaceId);
    else base.delete(namespaceId);
    setDraft(base);
    setValidationError(null);
  }

  const dirty =
    effective !== null &&
    savedIds !== null &&
    (effective.size !== savedIds.length || savedIds.some((id) => !effective.has(id)));

  function handleSave(): void {
    if (effective === null) return;
    setValidationError(null);
    update.mutate(
      // Registry order, so the saved set reads the same way the page does.
      { namespace_ids: (namespaces.data ?? []).map((row) => row.id).filter((id) => effective.has(id)) },
      {
        onSuccess: () => {
          setDraft(null);
          toast.success(t('default_key_namespaces.toast.saved'));
        },
        onError: (error) => {
          setValidationError(coerceErrorMessage(error));
        },
      },
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-6" data-testid="default-key-namespaces-page">
        <PageHeader
          title={t('default_key_namespaces.title')}
          sub={t('default_key_namespaces.subtitle')}
        />
        <EmptyState
          title={t('default_key_namespaces.forbidden')}
          testId="default-key-namespaces-forbidden"
        />
      </div>
    );
  }

  const pending = configured.isPending || namespaces.isPending;
  const error = configured.isError ? configured.error : namespaces.isError ? namespaces.error : null;

  return (
    <div className="flex flex-col gap-6" data-testid="default-key-namespaces-page">
      <PageHeader
        title={t('default_key_namespaces.title')}
        sub={t('default_key_namespaces.subtitle')}
      />
      <Panel
        caption={t('default_key_namespaces.panel')}
        testId="default-key-namespaces-panel"
      >
        {pending ? (
          <div className="p-4">
            <Skeleton className="h-40 w-full" />
          </div>
        ) : error !== null ? (
          <EmptyState
            title={t('default_key_namespaces.load_error')}
            hint={coerceErrorMessage(error)}
            action={
              <Button
                onClick={() => {
                  void configured.refetch();
                  void namespaces.refetch();
                }}
              >
                {t('common.retry')}
              </Button>
            }
            testId="default-key-namespaces-error"
          />
        ) : (
          <div className="flex flex-col gap-4 p-4">
            <p className="m-0 text-label text-paper-low">
              {t('default_key_namespaces.hint')}
            </p>
            <div className="flex flex-col gap-2">
              {(namespaces.data ?? []).map((namespace) => (
                <span
                  key={namespace.id}
                  className="inline-flex items-center justify-between rounded-lg border border-line-soft bg-well px-3 py-2"
                >
                  <Checkbox
                    checked={effective?.has(namespace.id) ?? false}
                    onCheckedChange={(checked) => toggle(namespace.id, checked)}
                    label={`${namespace.slug} · ${namespace.display_name}`}
                    testId={`default-key-namespace-${namespace.id}`}
                  />
                  <span className="text-label text-paper-low">
                    {t('default_key_namespaces.sku_count', {
                      count: String(liveSkuCounts.get(namespace.id) ?? 0),
                    })}
                  </span>
                </span>
              ))}
            </div>
            {validationError !== null ? (
              <p
                role="alert"
                data-testid="default-key-namespaces-validation-error"
                className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
              >
                {validationError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(null);
                  setValidationError(null);
                }}
                disabled={!dirty || update.isPending}
                data-testid="default-key-namespaces-reset"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={update.isPending}
                disabled={!dirty}
                data-testid="default-key-namespaces-save"
              >
                {t('default_key_namespaces.save')}
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
